import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Hand, Citrus, Recycle, MapPin } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import useSeo, { SITE } from "../hooks/useSeo";

const TITLE = "Chi siamo | Guatemalan Spirit, artigianalità a Salerno";
const DESCRIPTION = "Guatemalan Spirit, marchio di BOTTY srl a Salerno: liquori artigianali, cocktail in fusto e preparati di frutta tra Costiera Amalfitana e Cilento.";
const JSON_LD = [{
  "@context": "https://schema.org", "@type": "AboutPage", name: TITLE,
  url: `${SITE}/chi-siamo`, description: DESCRIPTION, inLanguage: "it-IT",
  mainEntity: {
    "@type": "Organization", name: "Guatemalan Spirits & Fruit Juice",
    alternateName: "Guatemalan Spirit", legalName: "BOTTY srl", url: `${SITE}/`,
    logo: `${SITE}/logo.png`,
    address: { "@type": "PostalAddress", streetAddress: "via Posidonia 26", postalCode: "84100", addressLocality: "Salerno", addressRegion: "SA", addressCountry: "IT" },
  },
}, {
  "@context": "https://schema.org", "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Chi siamo", item: `${SITE}/chi-siamo` },
  ],
}];

const VALUES = [
  { key: "cura", icon: Hand, title: "Il tempo della cura", text: "Macerazioni lente e dosaggi curati: il lavoro artigianale parte dalla ricetta e dall’attenzione a ogni preparazione." },
  { key: "frutta", icon: Citrus, title: "La frutta, protagonista", text: "Limoni della Costiera, agrumi e botaniche del Cilento. Per le nostre granite e i succhi scegliamo la frutta, senza coloranti né aromi artificiali." },
  { key: "riuso", icon: Recycle, title: "Il valore del riuso", text: "I cocktail arrivano in fusti Cornelius da 19 litri in acciaio inox, con vuoto a rendere. Un formato riutilizzabile pensato per il servizio nei locali." },
];

function BrandHero() {
  const reducedMotion = useReducedMotion();
  return (
    <section className="max-w-7xl mx-auto px-6 pt-8 pb-16 lg:pb-24" data-testid="chi-siamo-hero">
      <nav aria-label="Percorso di navigazione" className="flex items-center gap-3 text-xs text-stone-500 mb-10 lg:mb-14" data-testid="chi-siamo-breadcrumb">
        <Link to="/" className="hover:text-[#0F4C81] transition-colors" data-testid="chi-siamo-home-link">Home</Link>
        <span aria-hidden="true">/</span><span aria-current="page">Chi siamo</span>
      </nav>
      <div className="grid lg:grid-cols-5 gap-10 lg:gap-16 items-center">
        <motion.div className="lg:col-span-3 min-w-0" initial={reducedMotion ? false : { opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
          <p className="eyebrow text-[#C2593F] mb-5" data-testid="chi-siamo-eyebrow">Chi siamo · Salerno, Campania</p>
          <h1 className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] text-[#0B2A45]" data-testid="chi-siamo-h1">Guatemalan Spirit.<br /><span className="italic text-[#C2593F]">Una storia artigianale.</span></h1>
          <p className="mt-6 max-w-xl text-sm md:text-base leading-relaxed text-stone-600" data-testid="chi-siamo-intro">Siamo una piccola azienda salernitana. Con Guatemalan Spirits &amp; Fruit Juice, il marchio di BOTTY srl, portiamo nei liquori, nei cocktail e nei preparati di frutta la cura del lavoro artigianale e il legame con il nostro territorio.</p>
          <Button asChild className="mt-8 rounded-full bg-[#0F4C81] hover:bg-[#0A365C] h-12 px-6">
            <Link to="/negozio" data-testid="chi-siamo-shop-cta">Scopri i nostri liquori <ArrowRight aria-hidden="true" /></Link>
          </Button>
        </motion.div>
        <aside className="lg:col-span-2 bg-[#0B2A45] rounded-[2rem] p-8 lg:p-10 text-white" data-testid="chi-siamo-brand-panel">
          <img src="/logo.png" alt="Logo Guatemalan Spirits & Fruit Juice" width="192" height="192" className="w-40 h-40 lg:w-48 lg:h-48 mx-auto rounded-full" data-testid="chi-siamo-brand-image" />
          <div className="border-t border-white/20 mt-8 pt-6">
            <p className="font-serif text-xl leading-snug">Piccoli per scelta.<br />Grandi nella cura.</p>
            <p className="mt-4 text-xs tracking-wider uppercase text-[#FDE68A]">Liquori · Fusti · Frutta · Collezione</p>
          </div>
        </aside>
      </div>
    </section>
  );
}

function OurStory() {
  return (
    <section className="border-y border-stone-200 bg-white/60" data-testid="chi-siamo-story">
      <div className="max-w-7xl mx-auto px-6 py-16 lg:py-20 grid lg:grid-cols-5 gap-8 lg:gap-16">
        <div className="lg:col-span-2"><p className="eyebrow text-[#C2593F] mb-3">Le nostre radici</p><h2 className="font-serif font-semibold text-base md:text-lg text-[#0B2A45]">Da Salerno, tra la Costiera Amalfitana e il Cilento</h2></div>
        <div className="lg:col-span-3 space-y-5 text-sm md:text-base text-stone-600 leading-relaxed" data-testid="chi-siamo-story-text">
          <p>Il punto di partenza di Guatemalan Spirit è Salerno, tra due coste che fanno parte della nostra identità. Il rapporto con questa terra si ritrova negli agrumi, nelle botaniche e nell’attenzione alle materie prime del territorio.</p>
          <p>La nostra storia si racconta attraverso ciò che facciamo: liquori in bottiglia come limoncello, meloncello e liquirizia; cocktail pronti alla spillatura; preparati per granita e succhi di frutta. Alla produzione affianchiamo una selezione di bottiglie da collezione.</p>
          <p>Guatemalan Spirit si rivolge a chi cerca una bottiglia da gustare o regalare e agli operatori di bar, ristoranti e lidi. Per i professionisti, alla fornitura di fusti e preparati affianchiamo spillatori e granitori in comodato d’uso.</p>
          <Link to="/listino" className="inline-flex items-center gap-2 font-semibold text-[#0F4C81] hover:text-[#C2593F] transition-colors" data-testid="chi-siamo-b2b-link">Scopri il listino per operatori <ArrowRight className="w-4 h-4 shrink-0" aria-hidden="true" /></Link>
        </div>
      </div>
    </section>
  );
}

function OurValues() {
  return (
    <section className="max-w-7xl mx-auto px-6 py-16 lg:py-20" data-testid="chi-siamo-values">
      <p className="eyebrow text-[#C2593F] mb-3">Il nostro modo di lavorare</p>
      <h2 className="font-serif font-semibold text-base md:text-lg text-[#0B2A45] mb-8">La qualità è fatta di scelte quotidiane</h2>
      <div className="divide-y divide-stone-200">
        {VALUES.map(({ key, icon: Icon, title, text }, i) => (
          <article key={key} className="grid md:grid-cols-5 gap-5 md:gap-10 py-7" data-testid={`chi-siamo-value-${key}`}>
            <div className="md:col-span-2 flex items-center gap-4"><span className="font-mono text-xs text-stone-400">0{i + 1}</span><Icon className="w-5 h-5 text-[#C2593F] shrink-0" aria-hidden="true" /><h3 className="font-serif text-lg text-[#0B2A45]">{title}</h3></div>
            <p className="md:col-span-3 text-sm md:text-base text-stone-600 leading-relaxed">{text}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function CompanyDetails() {
  return (
    <section className="max-w-7xl mx-auto px-6 pb-16 lg:pb-24" data-testid="chi-siamo-company">
      <div className="rounded-2xl border border-[#EAB308]/30 bg-[#FEF9C3]/50 p-6 md:p-10 grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3">
          <p className="eyebrow text-[#854D0E] mb-3">Le persone, prima degli ordini</p>
          <h2 className="font-serif font-semibold text-base md:text-lg text-[#0B2A45]">Conosciamoci, un prodotto alla volta</h2>
          <p className="text-sm md:text-base text-stone-600 leading-relaxed mt-4 max-w-lg" data-testid="chi-siamo-contact-text">Vuoi conoscere meglio Guatemalan Spirit o scegliere una fornitura per il tuo locale? Scrivici per informazioni sui prodotti e sulle soluzioni dedicate agli operatori.</p>
          <Button asChild className="rounded-full bg-[#0F4C81] hover:bg-[#0A365C] h-12 px-6 mt-6"><Link to="/contatti" data-testid="chi-siamo-contact-cta">Parla con noi <ArrowRight aria-hidden="true" /></Link></Button>
        </div>
        <div className="lg:col-span-2 border-t lg:border-t-0 lg:border-l border-[#854D0E]/20 pt-6 lg:pt-0 lg:pl-10 text-sm">
          <div className="flex items-center gap-2 font-semibold text-[#0B2A45] mb-5"><MapPin className="w-4 h-4" aria-hidden="true" /> BOTTY srl</div>
          <dl className="space-y-5 text-stone-600" data-testid="chi-siamo-addresses">
            <div><dt className="font-semibold text-[#0B2A45] mb-1">Sede legale</dt><dd>via Posidonia 26<br />84100 Salerno (SA)</dd></div>
            <div><dt className="font-semibold text-[#0B2A45] mb-1">Stabilimento di produzione</dt><dd>via Irno 42/44<br />Pontecagnano Faiano (SA)</dd></div>
          </dl>
          <p className="text-xs text-stone-600 leading-relaxed mt-5" data-testid="chi-siamo-visit-note">Per informazioni sull’accesso allo stabilimento, contattaci prima di recarti sul posto.</p>
        </div>
      </div>
    </section>
  );
}

export default function ChiSiamo() {
  useSeo({ title: TITLE, description: DESCRIPTION, path: "/chi-siamo", jsonLd: JSON_LD });
  return (
    <div className="paper-grain min-h-screen" data-testid="chi-siamo-page">
      <Navbar />
      <main><BrandHero /><OurStory /><OurValues /><CompanyDetails /></main>
      <Footer />
    </div>
  );
}
